import ResourcePanel from '@/components/resources/ResourcePanel';
import { classNames } from '@/lib/classNames';
import {
  nonOfficeMatchingResourcesFilter,
  officeMatchingResourcesFilter
} from '@/lib/utils-map';
import { IResource, IUserFull } from '@gettactic/api';
import { IFloorWithResources } from '@gettactic/hooks';
import { Combobox } from '@headlessui/react';
import { Disclosure } from '@headlessui/react';
import { ExclamationCircleIcon, XCircleIcon } from '@heroicons/react/outline';
import { QrcodeIcon } from '@heroicons/react/outline';
import {
  ExclamationIcon,
  InformationCircleIcon,
  SearchIcon,
  TrashIcon,
  XIcon
} from '@heroicons/react/solid';
import { useState } from 'react';
import {
  IoDesktopOutline as Desktop,
  IoKeypadOutline as Group,
  IoCar as Parking
} from 'react-icons/io5';
import { Button } from '@/components/ui/button';
import { useCustomParkingVerbiageOnWeb as useCustomParkingVerbiage } from '@/hooks/useCustomParkingVerbiage';
import ResourceSettingsProvider from '@/hooks/useResourceSettings';

type MapEditPanelProps = {
  floors: IFloorWithResources[] | undefined;
  floorSpecificResources: IResource[] | undefined;
  allOfficeResources: IResource[] | undefined;
  allMapResources: Array<string>;
  mapEditSelectedList: Array<string>;
  setMapEditSelected: (id: string) => void;
  clearMapEditSelected: () => void;
  setNonMatchingOffice: () => void;
  setNonMatchingFloor: () => void;
  setHasNFC: () => void;
  nonOfficeMatchingMapResources: Array<string>;
  nonFloorMatchingMapResources: Array<string>;
  unassignedWorkspaces: IResource[] | undefined;
  isTacticStaffAuthenticatedUser: boolean;
  notifyUpdateLabels?: (resWithLabels: IResource[]) => void;
  onSaved?: () => void;
};

function MapEditPanel({
  floors,
  floorSpecificResources,
  allOfficeResources,
  allMapResources,
  mapEditSelectedList,
  setMapEditSelected,
  clearMapEditSelected,
  setNonMatchingOffice,
  setNonMatchingFloor,
  setHasNFC,
  nonOfficeMatchingMapResources,
  nonFloorMatchingMapResources,
  unassignedWorkspaces,
  isTacticStaffAuthenticatedUser,
  notifyUpdateLabels,
  onSaved
}: MapEditPanelProps) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<IUserFull | IResource | null>();
  const [showUnassigned, setShowUnassigned] = useState(false);

  const customParkingVerbiage = useCustomParkingVerbiage();

  const filteredResources =
    query === '' || !floorSpecificResources
      ? []
      : floorSpecificResources.filter((item) => {
          return item.name.toLowerCase().includes(query.toLowerCase());
        });

  const nonEditableResources = allOfficeResources
    ? nonOfficeMatchingResourcesFilter(mapEditSelectedList, allOfficeResources)
    : [];
  const editableResources = allOfficeResources
    ? officeMatchingResourcesFilter(mapEditSelectedList, allOfficeResources)
    : [];

  function updateSearchQuery(query: string) {
    // If the search input is cleared/erased, clear selection
    if (query.length === 0) {
      setSelected(null);
    }
    setQuery(query);
  }

  function clearSearchQuery() {
    setSelected(null);
    setQuery('');
  }

  /**
   * Checks the item ID for reso_
   */
  function isResource(item: IUserFull | IResource) {
    return item.id.startsWith('reso_');
  }

  function selectSearchItem(item: IUserFull | IResource | undefined) {
    setSelected(item);
    if (item === undefined) {
      return;
    }

    // If the selected item is a user, try to see if the user is at the office
    // If they're at the office, select the desk they're sitting at
    if (isResource(item)) {
      item.id = item.id;
    }

    setMapEditSelected(item.id);
  }

  function resourceAlreadySelected(id: string) {
    return mapEditSelectedList.includes(id);
  }

  return (
    <>
      <ResourceSettingsProvider>
        <div className={'flex p-2 items-center justify-end'}>
          <Button size={'sm'} variant={'ghost'} onClick={clearMapEditSelected}>
            <XIcon className={'h-5 w-5'} />
          </Button>
        </div>
        <div className="relative min-h-[50px]">
          <div className="pointer-events-none fixed top-[7.0rem] right-7 z-20 flex">
            <div className="pointer-events-auto mt-6 w-screen max-w-[37.3rem]">
              <div className="flex h-full flex-col overflow-y-auto rounded-md">
                <Combobox
                  as="div"
                  className="transform divide-y divide-gray-100 overflow-hidden rounded-md border bg-white ring-1 ring-black ring-opacity-5 transition-all"
                  value={selected}
                  onChange={(item: IUserFull | IResource) =>
                    item ? selectSearchItem(item) : null
                  }
                >
                  <div className="relative">
                    <SearchIcon
                      className="pointer-events-none absolute top-3.5 left-4 h-5 w-5 text-gray-400"
                      aria-hidden="true"
                    />
                    <Combobox.Input
                      className="h-12 w-full border-0 bg-transparent pl-11 pr-4 text-gray-800 placeholder-gray-400 focus:ring-0 sm:text-sm"
                      placeholder="Search resources..."
                      onChange={(event) =>
                        updateSearchQuery(event.target.value)
                      }
                      displayValue={(item: IUserFull | IResource) =>
                        item ? item.name : ''
                      }
                    />
                    {query.length > 1 ? (
                      <XIcon
                        className="pointer-cursor absolute top-3.5 right-4 h-5 w-5 text-gray-400"
                        aria-hidden="true"
                        onClick={() => clearSearchQuery()}
                      />
                    ) : null}
                  </div>

                  {filteredResources.length > 0 ? (
                    <>
                      <Combobox.Options className="max-h-96 scroll-py-3 overflow-y-auto p-3">
                        <Combobox.Label
                          className={classNames(
                            filteredResources.length === 0 && 'hidden',
                            'text-lg font-bold text-secondary [.tactic-v2_&]:text-secondary-foreground'
                          )}
                        >
                          <>
                            Workspaces
                            <div className="relative h-3">
                              <div
                                className="absolute inset-0 flex items-center"
                                aria-hidden="true"
                              >
                                <div className="w-full border-t border-gray-300" />
                              </div>
                            </div>
                          </>
                        </Combobox.Label>
                        {filteredResources.map((resource: IResource) => (
                          <Combobox.Option
                            key={resource.id}
                            value={resource}
                            className={({ active }) =>
                              classNames(
                                'flex cursor-default select-none rounded-xl p-3',
                                active && 'bg-gray-100'
                              )
                            }
                          >
                            {({ active }) => (
                              <>
                                <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg">
                                  <div className="rounded-full bg-gray-500 p-3">
                                    {resource.type === 'parking_lot' ||
                                    resource.type === 'parking_space' ? (
                                      customParkingVerbiage !== null ? (
                                        <div className="h-5 w-5" />
                                      ) : (
                                        <Parking className="h-5 w-5 text-white" />
                                      )
                                    ) : resource.total_slots > 1 ? (
                                      <Group className="h-5 w-5 text-white" />
                                    ) : (
                                      <Desktop className="h-5 w-5 text-white" />
                                    )}
                                  </div>
                                </div>
                                <div className="ml-4 flex-auto">
                                  <p
                                    className={classNames(
                                      'text-sm font-medium',
                                      active ? 'text-gray-900' : 'text-gray-700'
                                    )}
                                  >
                                    {resource.name}
                                  </p>
                                  {resourceAlreadySelected(resource.id) ? (
                                    <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                                      Already selected
                                    </span>
                                  ) : null}
                                  {floors ? (
                                    <span className="text-xs text-gray-800">
                                      {
                                        floors.find(
                                          (x) => x.id === resource.parent_id
                                        )?.name
                                      }
                                    </span>
                                  ) : null}
                                </div>
                              </>
                            )}
                          </Combobox.Option>
                        ))}
                      </Combobox.Options>
                    </>
                  ) : null}

                  {query !== '' && filteredResources.length === 0 ? (
                    <div className="py-14 px-6 text-center text-sm sm:px-14">
                      <ExclamationCircleIcon
                        type="outline"
                        name="exclamation-circle"
                        className="mx-auto h-6 w-6 text-gray-400"
                      />
                      <p className="mt-4 font-semibold text-gray-900">
                        No results found
                      </p>
                      <p className="mt-2 text-gray-500">
                        No matches found for this search term. Please try again.
                      </p>
                    </div>
                  ) : null}
                </Combobox>
              </div>
            </div>
          </div>
        </div>
        <div className="relative">
          {!showUnassigned ? (
            <div className="flex justify-end pb-2">
              {/* <button onClick={setHasNFC}>
                  <div className="flex flex-col px-4 hover:border-gray-200 hover:cursor-pointer border border-transparent rounded-lg m-1">
                    <QrcodeIcon className="h-15 w-15 text-gray-400" aria-hidden="true" />
                    <p className="text-gray-800">Workspaces</p>
                  </div>
                </button>
                <button>
                  <div className="flex flex-col px-4 hover:border-gray-200 hover:cursor-pointer border border-transparent rounded-lg m-1">
                    <QrcodeIcon className="h-15 w-15 text-gray-400" aria-hidden="true" />
                    <p className="text-gray-800">Restrictions</p>
                  </div>
                </button> */}
              <button
                onClick={setHasNFC}
                className="mr-3 flex max-w-[180px] rounded-lg border border-transparent py-4 hover:cursor-pointer hover:border-gray-200"
              >
                <div className="flex items-center gap-2 px-4">
                  <QrcodeIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                  <p className="text-gray-800">Show NFC &amp; QR</p>
                </div>
              </button>
            </div>
          ) : null}
        </div>
        <div className="relative">
          {mapEditSelectedList.length > 0 && !nonEditableResources.length ? (
            <div className="flex flex-col">
              <div className="rounded-md bg-blue-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <InformationCircleIcon
                      className="h-5 w-5 text-blue-400"
                      aria-hidden="true"
                    />
                  </div>

                  {mapEditSelectedList.length === 1 ? (
                    <div className="ml-3 flex-1 md:flex md:justify-between">
                      <p className="text-sm text-blue-700">
                        One workspace selected. Select additional to bulk edit.
                      </p>
                      <p className="mt-3 text-sm md:mt-0 md:ml-6">
                        <button
                          onClick={clearMapEditSelected}
                          className="inline-flex items-center whitespace-nowrap font-medium text-blue-700 hover:text-blue-600"
                        >
                          Clear Selection <TrashIcon className="ml-1 h-4 w-4" />
                        </button>
                      </p>
                    </div>
                  ) : (
                    <div className="ml-3 flex-1 md:flex md:justify-between">
                      <p className="text-sm text-blue-700">
                        {mapEditSelectedList.length} workspaces selected.
                      </p>
                      <p className="mt-3 text-sm md:mt-0 md:ml-6">
                        <button
                          onClick={clearMapEditSelected}
                          className="inline-flex items-center whitespace-nowrap font-medium text-blue-700 hover:text-blue-600"
                        >
                          Clear Selection <TrashIcon className="ml-1 h-4 w-4" />
                        </button>
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex w-full items-center py-4">
                {editableResources.length === 1 ? (
                  <>
                    {editableResources[0].type === 'meeting_room' ? (
                      <img
                        className="m-auto"
                        src="https://cdn.gettactic.com/website/images/meeting_room.webp"
                        alt="meeting room"
                      />
                    ) : editableResources[0].type === 'parking_lot' ||
                      editableResources[0].type === 'parking_space' ? (
                      // If custom parking verbiage assigned - it is a locker, don't show the parking image
                      customParkingVerbiage !== null ? null : (
                        <img
                          className="m-auto"
                          src="https://cdn.gettactic.com/website/images/parking.webp"
                          alt="desk"
                        />
                      )
                    ) : (
                      <img
                        className="m-auto"
                        src="https://cdn.gettactic.com/website/images/desk.webp"
                        alt="desk"
                      />
                    )}
                  </>
                ) : (
                  <img
                    className="m-auto"
                    src="https://cdn.gettactic.com/website/images/assorted.webp"
                    alt="assorted"
                  />
                )}
              </div>

              {allOfficeResources && mapEditSelectedList.length ? (
                <ResourcePanel
                  notifyUpdateLabels={notifyUpdateLabels}
                  resources={editableResources}
                  onSaved={onSaved}
                />
              ) : null}
            </div>
          ) : (
            <>
              {isTacticStaffAuthenticatedUser &&
              unassignedWorkspaces &&
              unassignedWorkspaces.length ? (
                <Disclosure>
                  <div className="mb-2 rounded-md bg-yellow-50 p-4">
                    <div className="flex">
                      <div className="mt-1 flex-shrink-0">
                        <ExclamationIcon
                          className="h-5 w-5 text-yellow-400"
                          aria-hidden="true"
                        />
                      </div>
                      <div className="ml-3">
                        <Disclosure.Button>
                          <h3 className="text-sm font-medium text-yellow-800">
                            Unassigned Office Workspaces
                          </h3>
                        </Disclosure.Button>
                        <Disclosure.Panel>
                          <div className="mt-2 text-sm text-yellow-700">
                            <p>
                              There are {unassignedWorkspaces.length} workspaces
                              that belong to this floor that have not been
                              assigned to the map.
                            </p>
                            <div className="mt-2 flex justify-end">
                              <button
                                onClick={() =>
                                  setShowUnassigned(!showUnassigned)
                                }
                                className="text-yellow-800 underline"
                              >
                                See list
                              </button>
                            </div>
                          </div>
                        </Disclosure.Panel>
                      </div>
                    </div>
                  </div>
                </Disclosure>
              ) : null}
              {isTacticStaffAuthenticatedUser &&
              nonOfficeMatchingMapResources &&
              nonOfficeMatchingMapResources.length ? (
                <Disclosure>
                  <div className="mb-2 rounded-md bg-yellow-50 p-4">
                    <div className="flex">
                      <div className="mt-1 flex-shrink-0">
                        <ExclamationIcon
                          className="h-5 w-5 text-yellow-400"
                          aria-hidden="true"
                        />
                      </div>
                      <div className="ml-3">
                        <Disclosure.Button>
                          <h3 className="text-sm font-medium text-yellow-800">
                            Map Missing Matching Office Workspace
                          </h3>
                        </Disclosure.Button>
                        <Disclosure.Panel>
                          <div className="mt-2 text-sm text-yellow-700">
                            <div className="flex flex-col">
                              <p>
                                {nonOfficeMatchingMapResources.length} /{' '}
                                {allMapResources.length} of the workspace
                                circles on this map don't have a matching
                                resource ID in this office. Have you created and
                                assigned all workspaces?
                              </p>
                              <div className="mt-2 flex justify-end">
                                <button
                                  onClick={setNonMatchingOffice}
                                  className="text-yellow-800 underline"
                                >
                                  Highlight all mismatches
                                </button>
                              </div>
                            </div>
                          </div>
                        </Disclosure.Panel>
                      </div>
                    </div>
                  </div>
                </Disclosure>
              ) : null}
              {isTacticStaffAuthenticatedUser &&
              nonFloorMatchingMapResources &&
              nonFloorMatchingMapResources.length ? (
                <Disclosure>
                  <div className="mb-2 rounded-md bg-yellow-50 p-4">
                    <div className="flex">
                      <div className="mt-1 flex-shrink-0">
                        <ExclamationIcon
                          className="h-5 w-5 text-yellow-400"
                          aria-hidden="true"
                        />
                      </div>
                      <div className="ml-3">
                        <Disclosure.Button>
                          <h3 className="text-sm font-medium text-yellow-800">
                            Workspaces Not Assigned Properly
                          </h3>
                        </Disclosure.Button>
                        <Disclosure.Panel>
                          <div className="mt-2 text-sm text-yellow-700">
                            <div className="flex flex-col">
                              <p>
                                There are {nonFloorMatchingMapResources?.length}{' '}
                                workspaces on this map that either haven't been
                                assigned to this floor on have been assigned to
                                a different floor in this office.
                              </p>
                              <div className="mt-2 flex justify-end">
                                <button
                                  onClick={setNonMatchingFloor}
                                  className="text-yellow-800 underline"
                                >
                                  Highlight all mismatches
                                </button>
                              </div>
                            </div>
                          </div>
                        </Disclosure.Panel>
                      </div>
                    </div>
                  </div>
                </Disclosure>
              ) : null}
              {nonEditableResources?.length ? (
                <Disclosure defaultOpen={true}>
                  <div className="mb-2 rounded-md bg-red-50 p-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <XCircleIcon
                          className="h-5 w-5 text-red-400"
                          aria-hidden="true"
                        />
                      </div>
                      <div className="ml-3">
                        <Disclosure.Button>
                          <h3 className="text-sm font-medium text-red-800">
                            Non Editable Map Resource Selected
                          </h3>
                        </Disclosure.Button>
                        <Disclosure.Panel>
                          <div className="mt-2 text-sm text-red-700">
                            <div className="flex flex-col">
                              <p>
                                {nonEditableResources.length} of the workspaces
                                selected cannot be edited because they do not
                                match a corresponding office workspace.
                              </p>
                              <div className="mt-2 flex justify-end">
                                <button
                                  onClick={clearMapEditSelected}
                                  className="text-red-800 underline"
                                >
                                  Clear list
                                </button>
                              </div>
                            </div>
                          </div>
                        </Disclosure.Panel>
                      </div>
                    </div>
                  </div>
                </Disclosure>
              ) : null}
              <div className="mt-4 flex flex-col">
                <img
                  className="m-auto"
                  src="https://cdn.gettactic.com/website/images/assorted_medium.webp"
                  alt="assorted"
                />
                <h4 className="mt-2 px-8 text-center text-2xl font-bold text-gray-800">
                  To get started editing, select workspaces on the map or using
                  the search bar
                </h4>
              </div>
            </>
          )}
          {!mapEditSelectedList.length &&
          showUnassigned &&
          unassignedWorkspaces ? (
            <div className="mt-2 flex flex-col">
              <div className="relative">
                <div
                  className="absolute inset-0 flex items-center"
                  aria-hidden="true"
                >
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative my-4 flex justify-center">
                  <button onClick={() => setShowUnassigned(!showUnassigned)}>
                    <span className="bg-white px-2 text-sm text-gray-500">
                      Hide
                    </span>
                  </button>
                </div>
              </div>
              <h3 className="text-lg font-bold text-gray-800 underline">
                Unassigned Office Workspaces
              </h3>
              {unassignedWorkspaces.map((workspace) => (
                <button
                  onClick={() => setMapEditSelected(workspace.id)}
                  key={workspace.id}
                >
                  <div className="mt-1 flex w-full items-center justify-between rounded-lg border bg-white p-4 shadow-lg">
                    <p className="font-bold text-gray-800">{workspace.name}</p>
                    <p className="font-bold text-gray-800 underline">Edit</p>
                  </div>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </ResourceSettingsProvider>
    </>
  );
}

export default MapEditPanel;
